// frontend/src/hooks/useAnalytics.js
// React Hook for Google Analytics integration

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackPageView,
  GameAnalytics,
  UserProperties,
  trackTiming,
  trackEvent,
} from '../services/analytics';

export const useAnalytics = () => {
  const location = useLocation();
  const pageLoadTime = useRef(Date.now());
  const roundStartTime = useRef(null);
  const gameStartTime = useRef(null);

  // Track page views on route changes
  useEffect(() => {
    const pagePath = location.pathname;
    const pageTitle = getPageTitle(pagePath);
    trackPageView(pagePath, pageTitle);

    // Track time spent on previous page
    const timeOnPage = Date.now() - pageLoadTime.current;
    if (timeOnPage > 1000) {
      // Only track if spent more than 1 second
      trackTiming('user_engagement', 'time_on_page', timeOnPage, pagePath);
    }

    // Reset page load time
    pageLoadTime.current = Date.now();
  }, [location]);

  // Game creation tracking
  const trackGameCreated = useCallback((roomCode, settings = {}) => {
    GameAnalytics.trackGameCreated(roomCode, settings);
    UserProperties.setPlayerType('creator');
  }, []);

  // Game joining tracking
  const trackGameJoined = useCallback((roomCode, playerCount) => {
    GameAnalytics.trackGameJoined(roomCode, playerCount);
    UserProperties.setPlayerType('joiner');
  }, []);

  // Game start tracking with timing
  const trackGameStarted = useCallback((roomCode, playerCount) => {
    gameStartTime.current = Date.now();
    GameAnalytics.trackGameStarted(roomCode, playerCount);
  }, []);

  // Round tracking with timing
  const trackRoundStarted = useCallback(() => {
    roundStartTime.current = Date.now();
  }, []);

  const trackRoundCompleted = useCallback((roomCode, roundNumber) => {
    if (roundStartTime.current) {
      const roundDuration = Date.now() - roundStartTime.current;
      GameAnalytics.trackRoundCompleted(roomCode, roundNumber, roundDuration);
      roundStartTime.current = null;
    }
  }, []);

  // Card submission with timing
  const trackCardSubmitted = useCallback((roomCode) => {
    if (roundStartTime.current) {
      const submissionTime = Date.now() - roundStartTime.current;
      GameAnalytics.trackCardSubmitted(roomCode, submissionTime);
    }
  }, []);

  // Game completion with full metrics
  const trackGameCompleted = useCallback((roomCode, winner, playerCount, totalRounds) => {
    if (gameStartTime.current) {
      const gameDuration = Date.now() - gameStartTime.current;
      GameAnalytics.trackGameCompleted(roomCode, winner, playerCount, gameDuration, totalRounds);

      // Update user properties
      const gamesPlayed = parseInt(localStorage.getItem('gamesPlayed') || '0') + 1;
      localStorage.setItem('gamesPlayed', gamesPlayed.toString());
      UserProperties.setGamesPlayed(gamesPlayed);

      gameStartTime.current = null;
    }
  }, []);

  // Feature usage tracking
  const trackFeature = useCallback((featureName, details = {}) => {
    GameAnalytics.trackFeatureUsed(featureName, details);
  }, []);

  // Error tracking
  const trackError = useCallback((errorType, details = {}) => {
    trackEvent('error_occurred', {
      error_type: errorType,
      error_details: JSON.stringify(details),
      event_category: 'errors',
      event_label: errorType,
    });
  }, []);

  // Connection tracking
  const trackConnectionStatus = useCallback((status, roomCode = null) => {
    if (status === 'error' || status === 'disconnected') {
      GameAnalytics.trackConnectionError(status, roomCode);
    } else {
      trackEvent('connection_status', {
        status: status,
        room_code: roomCode,
        event_category: 'technical',
        event_label: status,
      });
    }
  }, []);

  // User engagement metrics
  const trackEngagement = useCallback((action, details = {}) => {
    trackEvent(`engagement_${action}`, {
      ...details,
      event_category: 'user_engagement',
      event_label: action,
    });
  }, []);

  // UI interaction tracking
  const trackClick = useCallback((elementName, elementType = 'button') => {
    trackEvent('ui_interaction', {
      element_name: elementName,
      element_type: elementType,
      event_category: 'user_interface',
      event_label: elementName,
    });
  }, []);

  return {
    // Game flow events
    trackGameCreated,
    trackGameJoined,
    trackGameStarted,
    trackRoundStarted,
    trackRoundCompleted,
    trackCardSubmitted,
    trackVoteCast: GameAnalytics.trackVoteCast,
    trackGameCompleted,

    // Feature tracking
    trackFeature,

    // Error/connection tracking
    trackError,
    trackConnectionStatus,
    trackPlayerDropoff: GameAnalytics.trackPlayerDropoff,

    // User tracking
    trackUserSignup: GameAnalytics.trackUserSignup,
    trackUserLogin: GameAnalytics.trackUserLogin,

    // Engagement tracking
    trackEngagement,
    trackClick,

    // Raw event tracking
    trackEvent,

    // User properties
    setUserProperties: UserProperties,
  };
};

// Helper function to get page titles
function getPageTitle(pathname) {
  const titles = {
    '/': 'Landing Page',
    '/claim-token': 'Token Claiming',
    '/create-room': 'Create Room',
    '/join-game': 'Join Game',
    '/game-lobby': 'Game Lobby',
    '/game-room': 'Game Room',
  };

  return titles[pathname] || 'Unknown Page';
}

// Hook for tracking component visibility
export const useVisibilityTracking = (componentName, threshold = 0.5) => {
  const elementRef = useRef(null);
  const hasBeenVisible = useRef(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasBeenVisible.current) {
            hasBeenVisible.current = true;
            trackEvent('element_visible', {
              element_name: componentName,
              event_category: 'user_interface',
              event_label: 'visibility',
            });
          }
        });
      },
      { threshold }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [componentName, threshold]);

  return elementRef;
};

// Hook for tracking time spent on component
export const useTimeTracking = (componentName) => {
  useEffect(() => {
    const start = Date.now();

    return () => {
      const timeSpent = Date.now() - start;
      if (timeSpent > 1000) {
        // Only track if more than 1 second
        trackTiming('component_engagement', componentName, timeSpent, 'time_spent');
      }
    };
  }, [componentName]);
};

// Hook for A/B testing tracking
export const useABTest = (testName, variant) => {
  useEffect(() => {
    trackEvent('ab_test_exposure', {
      test_name: testName,
      variant: variant,
      event_category: 'experiments',
      event_label: `${testName}_${variant}`,
    });
  }, [testName, variant]);

  const trackConversion = useCallback(
    (conversionType) => {
      trackEvent('ab_test_conversion', {
        test_name: testName,
        variant: variant,
        conversion_type: conversionType,
        event_category: 'experiments',
        event_label: `${testName}_${variant}_${conversionType}`,
      });
    },
    [testName, variant]
  );

  return { trackConversion };
};
